import { BasicLayout, Orientation, OrientationMap, Orientor } from '@1x2network/layout'
import { applySpecification, Component, ContainerSpecification } from '@1x2network/pixi_assets'
import { Container, IPointData } from 'pixi.js'
import { Signal } from 'typed-signals'
import { PrizeConfiguration, PrizeKey } from '../../model/SetupModel'
import { JackpotWidget, JackpotWidgetSpec } from './JackpotWidget'

/**
 * Group of jackpot widgets to display jackpot awards
 * @author Jordan Harrison - <jordan.harrison@1x2network.com>
 * @version 1.0.0
 */

export class JackpotWidgetGroup extends Component<JackpotWidgetGroupSpec> {
    private jackpots: JackpotRecord<JackpotWidget>
    private orientor: Orientor<JackpotRecord<IPointData>>
    private introTimeline?: gsap.core.Timeline
    private layout: BasicLayout

    public readonly onWidgetLand = new Signal()

    get finalPositions() {
        return this.orientor.active
    }

    get widgets() {
        return this.jackpots
    }

    constructor(parent: Container, layout: BasicLayout) {
        super({ spec: 'jackpotWidgetGroup', parent })

        this.jackpots = {
            major: new JackpotWidget(this.specification.major, this),
            mega: new JackpotWidget(this.specification.mega, this),
            mini: new JackpotWidget(this.specification.mini, this),
            minor: new JackpotWidget(this.specification.minor, this),
        }
        this.orientor = new Orientor({
            property: this.specification.transforms,
            layout,
            onOrient: (active) => {
                this.reposition(active)
                this.introTimeline?.progress(1)
                this.introTimeline?.kill()
            },
        })

        this.layout = layout
    }

    /**
     * update the current stake for each jackpot widget
     * @param stake new stake
     */
    updateStake(stake: number) {
        const keys = Object.keys(this.jackpots)
        for (const key of keys) {
            const jackpotKey = key as keyof JackpotRecord<unknown>
            const widget = this.jackpots[jackpotKey]

            widget.stake = stake
        }
    }

    /**
     * update the prize value for each corresponding jackpot widget
     * @param prizes prize configuration object
     */
    updateValues(prizes: Record<PrizeKey, PrizeConfiguration>) {
        for (const key of Object.keys(prizes) as Array<PrizeKey>) {
            const jackpotKey = key.toLowerCase() as keyof JackpotRecord<unknown>
            const widget = this.jackpots[jackpotKey]

            widget.value = prizes[key].timesStake
        }
    }

    /**
     * reposition widgets on orientation change
     * @param active active transforms
     */
    reposition(active: JackpotTransforms[Orientation]) {
        const keys = Object.keys(this.jackpots)
        for (const key of keys) {
            const jackpotKey = key as keyof JackpotRecord<unknown>
            const widget = this.jackpots[jackpotKey]
            const specification = active[jackpotKey]
            applySpecification(widget, specification)
        }
    }
}

export interface JackpotWidgetGroupSpec extends ContainerSpecification, JackpotRecord<JackpotWidgetSpec> {
    transforms: JackpotTransforms
}

type JackpotTransforms = OrientationMap<JackpotRecord<IPointData>>

export interface JackpotRecord<T> {
    mini: T
    minor: T
    major: T
    mega: T
}
